/**
 * PhotoCache — IndexedDB-based photo caching for kiosk resilience.
 * Caches captured photos immediately so they survive crashes/reloads.
 *
 * v3.7.15 hardening:
 *   - Every public function is GUARANTEED to return a resolved Promise.
 *     No matter what IndexedDB / Chromium throws ("Write permission
 *     denied", "QuotaExceededError", "InvalidStateError"), the caller
 *     never sees a rejection. This prevents the "Unhandled promise
 *     rejection: Write permission denied" overlay that fired on booths
 *     whose Windows profile blocks AppData IndexedDB writes (antivirus,
 *     Group Policy, etc).
 *   - Photo caching is a "nice to have" (crash recovery). When it fails,
 *     the kiosk should continue working normally without any visible
 *     error to the customer. Internal warnings still go to console for
 *     diagnostics.
 */

const DB_NAME = 'piczoo_photo_cache';
const DB_VERSION = 1;
const STORE_NAME = 'sessions';

// Memoised "cache unavailable" flag so we don't spam the console with the
// same warning on every photo capture.
let _cacheDisabled = false;
function disableCache(reason) {
  if (!_cacheDisabled) {
    _cacheDisabled = true;
    // eslint-disable-next-line no-console
    console.warn(`[photoCache] disabled: ${reason}`);
  }
}

function openDB() {
  return new Promise((resolve) => {
    if (_cacheDisabled || typeof indexedDB === 'undefined') {
      return resolve(null);
    }
    let settled = false;
    const safeResolve = (v) => { if (!settled) { settled = true; resolve(v); } };

    let request;
    try {
      request = indexedDB.open(DB_NAME, DB_VERSION);
    } catch (e) {
      disableCache(e?.message || 'open() threw synchronously');
      return safeResolve(null);
    }
    request.onupgradeneeded = (e) => {
      try {
        const db = e.target.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        }
      } catch (err) {
        // createObjectStore can throw on read-only profiles — handled below
        // when the request fails or the upgrade transaction aborts.
        disableCache(err?.message || 'onupgradeneeded threw');
      }
    };
    request.onsuccess = () => safeResolve(request.result);
    request.onerror = () => {
      disableCache(request.error?.message || 'open() onerror');
      safeResolve(null);
    };
    request.onblocked = () => {
      disableCache('open() blocked');
      safeResolve(null);
    };
  });
}

export async function cachePhoto(kioskId, photoIndex, photoDataUrl) {
  try {
    const db = await openDB();
    if (!db) return;
    try {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);

      const sessionKey = `current_${kioskId}`;
      const existing = await new Promise((res) => {
        try {
          const req = store.get(sessionKey);
          req.onsuccess = () => res(req.result);
          req.onerror = () => res(null);
        } catch (_) {
          res(null);
        }
      });

      const session = existing || {
        id: sessionKey,
        kioskId,
        photos: [],
        createdAt: Date.now(),
      };

      session.photos[photoIndex] = photoDataUrl;
      session.updatedAt = Date.now();

      await new Promise((res) => {
        try {
          const req = store.put(session);
          req.onsuccess = () => res();
          req.onerror = () => res();  // swallow: cache failure is non-fatal
        } catch (_) {
          res();
        }
      });

      tx.oncomplete = () => { try { db.close(); } catch (_) { /* ignore */ } };
      tx.onerror = () => disableCache(tx.error?.message || 'tx error');
      tx.onabort = () => disableCache('tx abort');
    } catch (err) {
      disableCache(err?.message || 'cachePhoto inner threw');
      try { db.close(); } catch (_) { /* ignore */ }
    }
  } catch (err) {
    disableCache(err?.message || 'cachePhoto outer threw');
  }
}

export async function getCachedSession(kioskId) {
  try {
    const db = await openDB();
    if (!db) return null;
    try {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);

      const session = await new Promise((res) => {
        try {
          const req = store.get(`current_${kioskId}`);
          req.onsuccess = () => res(req.result);
          req.onerror = () => res(null);
        } catch (_) {
          res(null);
        }
      });

      try { db.close(); } catch (_) { /* ignore */ }
      if (!session) return null;

      // Expire stale sessions (older than 30 minutes)
      if (Date.now() - session.createdAt > 30 * 60 * 1000) {
        await clearCachedSession(kioskId);
        return null;
      }

      // Only return if there are actual photos
      const validPhotos = session.photos.filter(Boolean);
      if (validPhotos.length === 0) return null;

      return session;
    } catch (err) {
      disableCache(err?.message || 'getCachedSession inner threw');
      try { db.close(); } catch (_) { /* ignore */ }
      return null;
    }
  } catch (_) {
    return null;
  }
}

export async function clearCachedSession(kioskId) {
  try {
    const db = await openDB();
    if (!db) return;
    try {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).delete(`current_${kioskId}`);
      tx.oncomplete = () => { try { db.close(); } catch (_) { /* ignore */ } };
      tx.onerror = () => { try { db.close(); } catch (_) { /* ignore */ } };
      tx.onabort = () => { try { db.close(); } catch (_) { /* ignore */ } };
    } catch (err) {
      disableCache(err?.message || 'clearCachedSession inner threw');
      try { db.close(); } catch (_) { /* ignore */ }
    }
  } catch (_) {
    /* swallow */
  }
}
